/**
 * Clearing-depth highlight — reddens the chosen depth contour(s) on the chart as
 * a do-not-cross isobath. The contours live in the per-cell vector sources, so
 * for each `cell-*` source we keep a thin red line layer filtered to the DEPCNT
 * features whose charted depth value (attrs.depthM = VALDCO) is one of the active
 * clearing-depth values. The filter is rebuilt whenever the set of values
 * changes; with no values it matches nothing (hidden).
 */
import type { Map as MaplibreMap, FilterSpecification } from 'maplibre-gl';
import type { Palette } from '@/store';
import { getPalette } from './style/palettes';

/** The first overlay layer id, so the highlight sits under the boat/route/pilotage. */
function firstOverlayLayerId(map: MaplibreMap): string | undefined {
  const layers = map.getStyle()?.layers ?? [];
  for (const l of layers) {
    if (/^(ownship|route|pilotage|marks|safety|tide|objects)/.test(l.id)) return l.id;
  }
  return undefined;
}

function filterFor(values: number[]): FilterSpecification {
  if (!values.length) {
    return ['==', ['get', 'objClass'], '___acp_no_clearing_depth___'] as FilterSpecification;
  }
  return [
    'all',
    ['==', ['get', 'objClass'], 'DEPCNT'],
    ['in', ['to-number', ['coalesce', ['get', 'depthM', ['get', 'attrs']], -9999]], ['literal', values]],
  ] as FilterSpecification;
}

/**
 * Ensure each cell source has its clearing-depth highlight layer and set its
 * filter to the active values. Safe to call repeatedly; it adds the layer once
 * per source and just updates the filter after.
 */
export function updateClearingDepthHighlight(
  map: MaplibreMap,
  values: number[],
  palette: Palette,
): void {
  // Style-spec-ready gate (not isStyleLoaded(), which the running sim keeps false).
  const style = map.getStyle();
  if (!style?.sources) return;
  const colour = getPalette(palette).CHBLK_RED;
  const beforeId = firstOverlayLayerId(map);
  const filter = filterFor(values);
  for (const srcId of Object.keys(style.sources)) {
    if (!srcId.startsWith('cell-')) continue;
    const layerId = `${srcId}-clrdep`;
    if (map.getLayer(layerId)) {
      map.setFilter(layerId, filter);
    } else {
      map.addLayer(
        {
          id: layerId,
          type: 'line',
          source: srcId,
          filter,
          paint: {
            'line-color': colour,
            'line-width': ['interpolate', ['linear'], ['zoom'], 8, 1.5, 14, 3.2],
            'line-opacity': 0.95,
          },
        },
        beforeId,
      );
    }
  }
}
